import { useEffect, useState } from "react";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Link, useParams } from "react-router-dom";
import {
  MovieCastAPI,
  MovieDetailsAPI,
  MovieReviewsAPI,
  MovieSimilarAPI,
  MovieTrailerAPI,
  RecommendedMovieAPI,
} from "../constants/api";
import Loading from "./Loading";
import AccordionReviews from "./AccordionReviews";
import MovieDetailsSkeleton from "./MovieDetailsSkeleton";
import { Star, Calendar, Clock, Globe, Film, Play, Users, ChevronRight } from "lucide-react";

function formatDate(date) {
  if (date instanceof Date && !isNaN(date.getTime())) {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } else {
    return "Invalid Date";
  }
}

function formatRuntime(runtime) {
  if (typeof runtime === "number" && runtime > 0) {
    const hours = Math.floor(runtime / 60);
    const minutes = runtime % 60;
    return `${hours}h ${minutes}min`;
  } else {
    return "N/A";
  }
}
const MovieDetails = () => {
  const [movieDetails, setMovieDetails] = useState();
  const [loading, setLoading] = useState(true);
  const [trailer, setTrailer] = useState([]);
  const [cast, setCast] = useState([]);
  const [similarMovies, setSimilarMovies] = useState([]);
  const [recommendedMovies, setRecommended] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [server1, setServer1] = useState(true);
  const [server2, setServer2] = useState(false);

  const { id } = useParams();

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        // Fetch all data in parallel
        const [details, trailerData, castData, similarData, recommendedData, reviewsData] = await Promise.all([
          MovieDetailsAPI(id),
          MovieTrailerAPI(id),
          MovieCastAPI(id),
          MovieSimilarAPI(id),
          RecommendedMovieAPI(id),
          MovieReviewsAPI(id),
        ]);

        // Set all state
        setMovieDetails(details);
        setTrailer(trailerData?.results || []);
        setCast(castData?.cast || []);
        setSimilarMovies(similarData?.results || []);
        setRecommended(recommendedData?.results || []);
        setReviews(reviewsData?.results || []);
      } catch (error) {
        console.error("Error fetching movie details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [id]);

  const releaseDate = movieDetails?.release_date
    ? new Date(movieDetails.release_date)
    : undefined;

  console.log(id);
  if (loading) {
    return <MovieDetailsSkeleton />;
  }

  return (
    <section className="bg-black min-h-screen">
      {/* Hero Section with Backdrop */}
      <div className="relative w-full h-[auto] min-h-[60vh] md:h-[80vh] overflow-hidden pt-16 md:pt-0">
        {movieDetails?.backdrop_path && (
          <>
            <div 
              className="absolute inset-0 bg-cover bg-no-repeat"
              style={{
                backgroundImage: `url(https://image.tmdb.org/t/p/original${movieDetails.backdrop_path})`,
                backgroundPosition: 'center top',
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent" />
          </>
        )}
        
        {/* Content Overlay */}
        <div className="relative z-10 h-full flex items-center md:items-end padding py-8 md:pb-12">
          <div className="w-full max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start md:items-end">
              {/* Poster */}
              <div className="flex-shrink-0">
                <img
                  src={`https://image.tmdb.org/t/p/w500${movieDetails?.poster_path}`}
                  alt={movieDetails?.title}
                  className="w-[160px] sm:w-[180px] md:w-[280px] rounded-lg shadow-2xl border-2 border-white/10"
                />
              </div>

              {/* Movie Info */}
              <div className="flex-1 text-white">
                <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold poppins mb-2">
                  {movieDetails?.title}
                </h1>
                {movieDetails?.tagline && (
                  <p className="text-white/80 mont text-base sm:text-lg md:text-xl mb-4 italic">
                    {movieDetails?.tagline}
                  </p>
                )}

                {/* Meta Info */}
                <div className="flex flex-wrap items-center gap-3 md:gap-4 lg:gap-6 mb-4">
                  <div className="flex items-center gap-2 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/10">
                    <Star className="text-yellow-400" size={18} fill="#FBBF24" />
                    <span className="mont font-semibold text-sm">{movieDetails?.vote_average?.toFixed(1)}</span>
                  </div>
                  
                  {releaseDate && (
                    <div className="flex items-center gap-2 text-white/90">
                      <Calendar size={16} />
                      <span className="mont text-sm">{formatDate(releaseDate)}</span>
                    </div>
                  )}
                  
                  {movieDetails?.runtime > 0 && (
                    <div className="flex items-center gap-2 text-white/90">
                      <Clock size={16} />
                      <span className="mont text-sm">{formatRuntime(movieDetails.runtime)}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 text-white/90">
                    <Users size={16} />
                    <span className="mont text-sm">{movieDetails?.vote_count?.toLocaleString()} votes</span>
                  </div>
                </div>

                {/* Genres */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {movieDetails?.genres?.map((item) => (
                    <span
                      key={item?.id}
                      className="bg-red/20 backdrop-blur-sm border border-red/50 text-white mont text-xs px-2.5 py-1 rounded-full"
                    >
                      {item?.name}
                    </span>
                  ))}
                </div>

                {/* Overview */}
                <p className="text-white/90 mont text-sm sm:text-base md:text-lg leading-relaxed max-w-3xl line-clamp-3">
                  {movieDetails?.overview}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="padding pt-8">
        {/* Full Overview */}
        <div className="mb-8">
          <h2 className="text-white poppins text-xl md:text-2xl font-semibold mb-3">Overview</h2>
          <p className="text-white/80 mont text-base md:text-lg leading-relaxed max-w-4xl">
            {movieDetails?.overview}
          </p>
        </div>

        {/* Additional Info Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {/* Production Countries */}
          {movieDetails?.production_countries?.length > 0 && (
            <div className="bg-gray/20 backdrop-blur-sm rounded-lg p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <Globe size={20} className="text-red" />
                <h3 className="text-white poppins font-semibold">Production Countries</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {movieDetails.production_countries.map((country) => (
                  <span
                    key={country?.name}
                    className="bg-red/20 border border-red/50 text-white mont text-sm px-2 py-1 rounded"
                  >
                    {country?.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Languages */}
          {movieDetails?.spoken_languages?.length > 0 && (
            <div className="bg-gray/20 backdrop-blur-sm rounded-lg p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <Globe size={20} className="text-red" />
                <h3 className="text-white poppins font-semibold">Languages</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {movieDetails.spoken_languages.map((language) => (
                  <span
                    key={language?.name}
                    className="bg-red/20 border border-red/50 text-white mont text-sm px-2 py-1 rounded"
                  >
                    {language?.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Status */}
          <div className="bg-gray/20 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <Film size={20} className="text-red" />
              <h3 className="text-white poppins font-semibold">Status</h3>
            </div>
            <p className="text-white/80 mont">{movieDetails?.status}</p>
          </div>
        </div>

        {/* Production Companies */}
        {movieDetails?.production_companies?.length > 0 && (
          <div className="mb-12">
            <h2 className="text-white poppins text-xl md:text-2xl font-semibold mb-4">Production Companies</h2>
            <div className="flex flex-wrap gap-4">
              {movieDetails.production_companies.map((company) => (
                <div
                  key={company?.id}
                  className="bg-gray/20 backdrop-blur-sm border border-white/10 rounded-lg px-4 py-2"
                >
                  <p className="text-white mont text-sm">{company?.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Trailer Section */}
        {trailer && trailer.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-1 w-12 bg-red"></div>
              <h2 className="text-white poppins text-2xl md:text-3xl font-bold">Trailers</h2>
              <span className="text-white/60 mont text-sm">({trailer.length})</span>
            </div>
            <Swiper
              spaceBetween={20}
              slidesPerView={1}
              navigation={true}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              breakpoints={{
                640: {
                  slidesPerView: 1.5,
                  spaceBetween: 16,
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 20,
                },
                1024: {
                  slidesPerView: 2.5,
                  spaceBetween: 24,
                },
                1280: {
                  slidesPerView: 3,
                  spaceBetween: 24,
                },
              }}
              modules={[Navigation, Pagination]}
              className="modern-swiper"
            >
              {trailer?.map((movieTrailer) => (
                <SwiperSlide key={movieTrailer?.id}>
                  <div className="group">
                    <div className="relative aspect-video rounded-lg overflow-hidden bg-gray/20 border border-white/10">
                      <iframe
                        className="w-full h-full"
                        src={`https://www.youtube.com/embed/${movieTrailer.key}`}
                        title={movieTrailer?.name}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                    <p className="text-white/80 mont text-sm mt-2 line-clamp-1">{movieTrailer?.name}</p>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        {/* Watch Movie Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-1 w-12 bg-red"></div>
            <h2 className="text-white poppins text-2xl md:text-3xl font-bold">Watch Full Movie</h2>
          </div>
          <p className="text-white/60 mont text-sm md:text-base mb-6 max-w-2xl">
            You can switch between Server 1 and Server 2 to enjoy the best movie playback experience.
          </p>

          {/* Modern Server Selector */}
          <div className="inline-flex bg-gray/20 backdrop-blur-sm border border-white/10 rounded-lg p-1 mb-6">
            <button
              onClick={() => {
                setServer1(true);
                setServer2(false);
              }}
              className={`px-6 py-2 rounded-md mont font-semibold text-sm transition-all duration-300 ${
                server1
                  ? "bg-red text-white shadow-lg shadow-red/30"
                  : "text-white/60 hover:text-white"
              }`}
            >
              Server 1
            </button>
            <button
              onClick={() => {
                setServer1(false);
                setServer2(true);
              }}
              className={`px-6 py-2 rounded-md mont font-semibold text-sm transition-all duration-300 ${
                server2
                  ? "bg-red text-white shadow-lg shadow-red/30"
                  : "text-white/60 hover:text-white"
              }`}
            >
              Server 2
            </button>
          </div>

          {/* Movie Player */}
          <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-black border border-white/10">
            {server1 && (
              <iframe
                src={`https://vidsrc-embed.ru/embed/movie?tmdb=${id}`}
                title="Movie Player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            )}
            {server2 && (
              <iframe
                src={`https://vidsrc.to/embed/movie/${id}`}
                title="Movie Player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            )}
          </div>
        </div>
        {/* Cast Section */}
        {cast && cast.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-1 w-12 bg-red"></div>
              <h2 className="text-white poppins text-2xl md:text-3xl font-bold">Cast</h2>
            </div>
            <Swiper
              spaceBetween={16}
              slidesPerView={2}
              navigation={true}
              breakpoints={{
                480: {
                  slidesPerView: 3,
                },
                640: {
                  slidesPerView: 4,
                },
                768: {
                  slidesPerView: 5,
                },
                1024: {
                  slidesPerView: 6,
                },
                1280: {
                  slidesPerView: 7,
                },
              }}
              modules={[Navigation]}
              className="modern-swiper"
            >
              {cast?.slice(0, 20).map((person) => (
                <SwiperSlide key={person?.cast_id}>
                  <div className="flex flex-col items-center text-center group">
                    <div className="relative mb-3 w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-2 border-white/10 group-hover:border-red transition-colors duration-300">
                      {person?.profile_path ? (
                        <img
                          src={`https://image.tmdb.org/t/p/w500${person?.profile_path}`}
                          alt={person?.original_name}
                          className="w-full h-full object-cover object-top"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray/40 flex items-center justify-center">
                          <Users className="text-white/40" size={32} />
                        </div>
                      )}
                    </div>
                    <p className="text-white poppins text-sm font-semibold mb-1 line-clamp-1">
                      {person?.original_name}
                    </p>
                    <p className="text-white/60 mont text-xs line-clamp-1">
                      {person?.character}
                    </p>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        {/* Reviews Section */}
        {reviews && reviews.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-1 w-12 bg-red"></div>
              <h2 className="text-white poppins text-2xl md:text-3xl font-bold">Reviews</h2>
            </div>
            <div className="bg-gray/10 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden">
              {reviews?.slice(0, 6)?.map((review) => (
                <AccordionReviews
                  key={review.author}
                  author={review?.author}
                  rating={review?.author_details?.rating}
                  content={review?.content}
                  avatar_path={review?.author_details?.avatar_path}
                  created_at={review?.created_at}
                />
              ))}
            </div>
          </div>
        )}

        {/* Similar Movies Section */}
        {similarMovies && similarMovies.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-1 w-12 bg-red"></div>
              <h2 className="text-white poppins text-2xl md:text-3xl font-bold">Similar Movies</h2>
            </div>
            <Swiper
              spaceBetween={20}
              slidesPerView={5}
              loop={similarMovies.length > 5}
              navigation={true}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              breakpoints={{
                230: {
                  slidesPerView: 2,
                  spaceBetween: 8,
                },
                480: {
                  slidesPerView: 3,
                  spaceBetween: 10,
                },
                640: {
                  slidesPerView: 3,
                  spaceBetween: 12,
                },
                768: {
                  slidesPerView: 4,
                  spaceBetween: 15,
                },
                1000: {
                  slidesPerView: 5,
                  spaceBetween: 18,
                },
                1280: {
                  slidesPerView: 6,
                  spaceBetween: 20,
                },
              }}
              modules={[Navigation, Pagination]}
              className="modern-swiper"
            >
              {similarMovies?.filter(movie => movie.poster_path).map((movie) => (
                <SwiperSlide key={movie?.id} className="modern-movie-slide">
                  <Link to={`/movies/${movie?.id}`} className="group">
                    <div className="relative overflow-hidden rounded-lg bg-gray/30 movie-card-wrapper">
                      <div className="relative aspect-[2/3] overflow-hidden bg-gradient-to-br from-gray/40 to-gray/20">
                        <img
                          src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`}
                          alt={movie?.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                            <Play className="text-white" size={28} fill="white" />
                          </div>
                        </div>
                        <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                          <Star className="text-yellow-400" size={12} fill="#FBBF24" />
                          <span className="text-white mont text-xs font-semibold">
                            {movie?.vote_average?.toFixed(1)}
                          </span>
                        </div>
                      </div>
                      <div className="p-3">
                        <h3 className="text-white mont font-semibold text-sm leading-tight line-clamp-2 group-hover:text-red transition-colors duration-300">
                          {movie?.title}
                        </h3>
                      </div>
                    </div>
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        {/* Recommended Movies Section */}
        {recommendedMovies && recommendedMovies.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-1 w-12 bg-red"></div>
              <h2 className="text-white poppins text-2xl md:text-3xl font-bold">Recommended Movies</h2>
            </div>
            <Swiper
              spaceBetween={20}
              slidesPerView={5}
              loop={recommendedMovies.length > 5}
              navigation={true}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              breakpoints={{
                230: {
                  slidesPerView: 2,
                  spaceBetween: 8,
                },
                480: {
                  slidesPerView: 3,
                  spaceBetween: 10,
                },
                640: {
                  slidesPerView: 3,
                  spaceBetween: 12,
                },
                768: {
                  slidesPerView: 4,
                  spaceBetween: 15,
                },
                1000: {
                  slidesPerView: 5,
                  spaceBetween: 18,
                },
                1280: {
                  slidesPerView: 6,
                  spaceBetween: 20,
                },
              }}
              modules={[Navigation, Pagination]}
              className="modern-swiper"
            >
              {recommendedMovies?.filter(movie => movie.poster_path).map((movie) => (
                <SwiperSlide key={movie?.id} className="modern-movie-slide">
                  <Link to={`/movies/${movie?.id}`} className="group">
                    <div className="relative overflow-hidden rounded-lg bg-gray/30 movie-card-wrapper">
                      <div className="relative aspect-[2/3] overflow-hidden bg-gradient-to-br from-gray/40 to-gray/20">
                        <img
                          src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`}
                          alt={movie?.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                            <Play className="text-white" size={28} fill="white" />
                          </div>
                        </div>
                        <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                          <Star className="text-yellow-400" size={12} fill="#FBBF24" />
                          <span className="text-white mont text-xs font-semibold">
                            {movie?.vote_average?.toFixed(1)}
                          </span>
                        </div>
                      </div>
                      <div className="p-3">
                        <h3 className="text-white mont font-semibold text-sm leading-tight line-clamp-2 group-hover:text-red transition-colors duration-300">
                          {movie?.title}
                        </h3>
                      </div>
                    </div>
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}
      </div>
    </section>
  );
};

export default MovieDetails;
